import React, {
  useEffect,
  useState,
  useMemo,
  useLayoutEffect,
  useCallback,
} from "react";
import ApiData from "../../api/ApiData";
import User from "../../api/User";
import { observer } from "mobx-react-lite";
import { ListAllReport } from "../../components/allreport/ListAllReport";
import { FilterByDay } from "../../components/company/FilterByDay";
import { ExportToExcel } from "../../Export/ExportXLSX";
import { ContainerSubTitleGrey } from "../ContainerSubTitleGrey";
import FilterData from "../../api/FilterData";
import stateFilter from "../../state/state.filter";
import { ReportsManagerFilterBar } from "./ReportsManagerFilterBar";
import {
  filterSubscriptionReportRows,
  managerExcelCellsForSubscriptionItem,
  saveReportsShowMineOnly,
  subscriptionDaysFromCalendar,
  useReportsShowMineOnly,
} from "../../utils/reportManagerFilterUtils";
import { useApplyReportTemplate } from "../../hooks/useApplyReportTemplate";
import { SaveReportTemplateButton } from "./SaveReportTemplateButton";
import ReportExportActionsColumn from "./ReportExportActionsColumn";
import { REPORT_TAB_SUBSCRIPTION } from "../../utils/reportTemplatesStorage";

export const ReportLicensing = observer(() => {
  const [period5Cls, setPeriod5Cls] = useState("btn_deactive_period");
  const [period15Cls, setPeriod15Cls] = useState("btn_deactive_period");
  const [period30Cls, setPeriod30Cls] = useState("btn_deactive_period");
  const [period60Cls, setPeriod60Cls] = useState("btn_deactive_period");
  const [period345Cls, setPeriod345Cls] = useState("btn_deactive_period");
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [calendar, setCalendar] = useState("");
  const [minDay, setMinDay] = useState("");
  const [search, setSearch] = useState("");
  const [showMineOnly, setShowMineOnly] = useReportsShowMineOnly();

  const handlerPeriodCalendar = (e) => {
    setCalendar(e.target.value);
    setPeriod5Cls("btn_deactive_period");
    setPeriod15Cls("btn_deactive_period");
    setPeriod30Cls("btn_deactive_period");
    setPeriod60Cls("btn_deactive_period");
    setPeriod345Cls("btn_deactive_period");
    ApiData.setParameters(
      "max_subscription",
      subscriptionDaysFromCalendar(e.target.value, minDay),
    );
  };

  useLayoutEffect(() => {
    const d = new Date();
    setMinDay(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  }, []);

  useEffect(() => {
    if (ApiData.companys === null) {
      ApiData.getDataCompanys();
    }
  }, []);

  useEffect(() => {
    FilterData.filterReportSubscription(ApiData.max_subscription);
    if (ApiData.max_subscription === 5) {
      setPeriod5Cls("btn_active_period");
    } else if (ApiData.max_subscription === 15) {
      setPeriod15Cls("btn_active_period");
    } else if (ApiData.max_subscription === 30) {
      setPeriod30Cls("btn_active_period");
    } else if (ApiData.max_subscription === 60) {
      setPeriod60Cls("btn_active_period");
    } else if (ApiData.max_subscription === 345) {
      setPeriod345Cls("btn_active_period");
    }
    setSelectedPeriod(ApiData.max_subscription);
  }, [ApiData.max_subscription, stateFilter.subscription_companies]);

  const selectPeriod = async (e) => {
    setCalendar("");
    setPeriod5Cls("btn_deactive_period");
    setPeriod15Cls("btn_deactive_period");
    setPeriod30Cls("btn_deactive_period");
    setPeriod60Cls("btn_deactive_period");
    setPeriod345Cls("btn_deactive_period");
    await ApiData.selectMaxSubscription(
      e?.target ? Number(e.target.textContent) : selectedPeriod,
    );
  };

  const applyTemplateFields = useCallback(
    async (fields) => {
      stateFilter.setParameter(
        "subscription_companies",
        Boolean(fields.subscriptionCompaniesOnly),
      );
      stateFilter.setLocalStorage(
        "subscription_companies",
        stateFilter.subscription_companies,
      );
      saveReportsShowMineOnly(Boolean(fields.showMineOnly));
      setShowMineOnly(Boolean(fields.showMineOnly));
      setSearch(fields.search ?? "");

      if (fields.calendar) {
        setCalendar(fields.calendar);
        setPeriod5Cls("btn_deactive_period");
        setPeriod15Cls("btn_deactive_period");
        setPeriod30Cls("btn_deactive_period");
        setPeriod60Cls("btn_deactive_period");
        setPeriod345Cls("btn_deactive_period");
        const days = subscriptionDaysFromCalendar(fields.calendar, minDay);
        await ApiData.selectMaxSubscription(days);
      } else {
        setCalendar("");
        await ApiData.selectMaxSubscription(Number(fields.maxPeriod) || 30);
      }
      await FilterData.filterReportSubscription(ApiData.max_subscription);
    },
    [minDay, setShowMineOnly],
  );

  useApplyReportTemplate(REPORT_TAB_SUBSCRIPTION, applyTemplateFields);

  const getFieldsSnapshot = useCallback(
    () => ({
      subscriptionCompaniesOnly: stateFilter.subscription_companies,
      maxPeriod: ApiData.max_subscription,
      calendar,
      showMineOnly,
      search,
    }),
    [calendar, search, showMineOnly],
  );

  const onClickSubscription = () => {
    stateFilter.setParameter(
      "subscription_companies",
      !stateFilter.subscription_companies,
    );
    stateFilter.setLocalStorage(
      "subscription_companies",
      stateFilter.subscription_companies,
    );
    selectPeriod({ target: { textContent: String(selectedPeriod) } });
  };

  const filteredSubscriptions = useMemo(() => {
    const base = ApiData.subscription_data ?? ApiData.subscription ?? [];
    if (!Array.isArray(base)) {
      return [];
    }
    return filterSubscriptionReportRows(base, {
      searchQuery: search,
      showMineOnly,
      currentUserId: User.user?.id,
      companys: ApiData.companys,
    });
  }, [
    ApiData.subscription,
    ApiData.subscription_data,
    ApiData.companys,
    search,
    showMineOnly,
    User.user?.id,
  ]);

  const excel = useMemo(() => {
    return filteredSubscriptions.map((el) => ({
      Компания: el.company_rel.name_company,
      ИНН: el.company_rel.inn,
      До: el.validity_date,
      Описание: el.descriptions,
      ...managerExcelCellsForSubscriptionItem(el, ApiData.companys),
    }));
  }, [filteredSubscriptions, ApiData.companys]);

  return (
    <section className="card reports-tab-panel-section" aria-label="Подписки">
      <header className="reports-panel-head">
        <h2 className="dash-card-title">Сводный отчёт по подпискам</h2>
        <p className="reports-panel-desc">
          Компании, у которых срок подписки истекает в выбранном периоде
        </p>
      </header>

      <div className="reports-toolbar">
        <ReportsManagerFilterBar
          search={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          showMineOnly={showMineOnly}
          onShowMineOnlyChange={setShowMineOnly}
        />
        <FilterByDay
          variant="v4"
          selectedPeriod={selectedPeriod}
          onClickSubscription={onClickSubscription}
          onClick={selectPeriod}
          className5={period5Cls}
          className15={period15Cls}
          className30={period30Cls}
          className60={period60Cls}
          className345={period345Cls}
          calendar={calendar}
          minDay={minDay}
          handlerPeriodCalendar={handlerPeriodCalendar}
        />
        <ReportExportActionsColumn>
          <SaveReportTemplateButton
            tab={REPORT_TAB_SUBSCRIPTION}
            getFieldsSnapshot={getFieldsSnapshot}
          />
          <ExportToExcel fileName="Отчёт по компаниям" excel={excel} />
        </ReportExportActionsColumn>
      </div>

      {ApiData.subscription !== null ? (
        <ListAllReport
          data={filteredSubscriptions}
          companys={ApiData.companys}
        />
      ) : (
        <ContainerSubTitleGrey text="Нет компаний соответствующих выбранному фильтру" />
      )}
    </section>
  );
});
